import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCategory, CATEGORIES } from "../mock";
import { ArticleCardCompact } from "./ArticleCards";
import { ArticlesAPI, normalizeArticle, SubscribeAPI, WireAPI } from "../lib/api";
import { Rss, Mail, ArrowUpRight, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";

const SectionHead = ({ children, kicker }) => (
  <div className="mb-4 pb-2 border-b-2 border-[#0B1F33] flex items-center justify-between">
    <div className="font-serif-display text-[20px] font-bold text-[#0B1F33]">{children}</div>
    {kicker && <div className="text-[10px] tracking-[0.25em] uppercase text-[#0891B2] font-semibold">{kicker}</div>}
  </div>
);

const Sidebar = ({ articles: incoming }) => {
  const [trending, setTrending] = useState(incoming || []);
  const [wire, setWire] = useState([]);
  const [wireLoading, setWireLoading] = useState(true);

  useEffect(() => {
    if (incoming && incoming.length) {
      setTrending(incoming.slice(0, 6));
      return;
    }
    ArticlesAPI.list({ limit: 6 })
      .then((d) => setTrending(d.map(normalizeArticle)))
      .catch(() => {});
  }, [incoming]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      WireAPI.list(10)
        .then((d) => { if (alive) { setWire(d.items || []); setWireLoading(false); } })
        .catch(() => { if (alive) setWireLoading(false); });
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000); // refresh every 5 min client-side
    return () => { alive = false; clearInterval(t); };
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    const email = e.currentTarget.email.value;
    try {
      await SubscribeAPI.add(email, "free");
      toast.success("Subscribed to the free Insights newsletter.");
      e.currentTarget.reset();
    } catch (err) {
      toast.error("Subscription failed. Please try again.");
    }
  };

  return (
    <aside className="space-y-10">
      <div className="bg-[#0B1F33] text-[#D7DEE5] p-6">
        <div className="flex items-center gap-2 text-[#0891B2]">
          <Mail className="w-4 h-4" />
          <span className="text-[10px] tracking-[0.3em] uppercase font-semibold">Free Newsletter</span>
        </div>
        <h3 className="mt-3 font-serif-display text-[22px] leading-tight font-bold">The Insights Weekly</h3>
        <p className="mt-2 text-[13px] text-[#D7DEE5]/75 leading-relaxed">Independent market research and educational commentary. No fluff, no product pitches.</p>
        <form onSubmit={subscribe} className="mt-4 space-y-2">
          <Input name="email" required type="email" placeholder="your@email.com" className="rounded-none bg-[#132f57]/50 border-[#1f3454] text-[#D7DEE5] placeholder:text-[#D7DEE5]/40 focus-visible:ring-0 focus:border-[#B8E9F1]" />
          <Button type="submit" className="w-full rounded-none bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] uppercase tracking-wider text-[11px] font-semibold">Get Insights</Button>
        </form>
      </div>

      <div>
        <SectionHead kicker="Last 24h">Most Read</SectionHead>
        <div>
          {trending.slice(0, 6).map((a, i) => (
            <ArticleCardCompact key={a.id} a={a} idx={i} />
          ))}
        </div>
      </div>

      <div>
        <SectionHead kicker="Live">The Wire</SectionHead>
        {wireLoading ? (
          <div className="flex items-center gap-2 text-[12px] text-neutral-500 py-6">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading live feed…
          </div>
        ) : wire.length === 0 ? (
          <div className="text-[12px] text-neutral-500 py-6">Feed unavailable. Please refresh.</div>
        ) : (
          <ul className="space-y-3" data-testid="wire-feed-list">
            {wire.map((f, i) => (
              <li key={f.link || i} className="group">
                <a
                  href={f.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`wire-item-${i}`}
                  className="block"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[#0891B2] font-semibold">
                    <span className="inline-flex items-center gap-1.5"><Rss className="w-3 h-3" />{f.source}</span>
                    <span className="font-mono-tick text-neutral-500">{f.time}</span>
                  </div>
                  <div className="mt-1 text-[14px] leading-snug text-[#0B1F33] group-hover:text-[#0891B2] transition-colors">
                    {f.title}
                    <ArrowUpRight className="w-3.5 h-3.5 inline ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SectionHead>Sections</SectionHead>
        <ul className="grid grid-cols-2 gap-1">
          {CATEGORIES.map((c) => (
            <li key={c.slug}>
              <Link to={`/section/${c.slug}`} className="block px-3 py-2 text-[12px] tracking-wider uppercase font-medium border border-neutral-300 hover:border-[#B8E9F1] hover:text-[#0891B2] transition-colors">
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;
